import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Button, 
  Upload, 
  message, 
  Table, 
  Tag, 
  Space, 
  Modal, 
  Form, 
  Input, 
  Select,
  Tabs,
  Row,
  Col,
  Statistic,
  Dropdown,
  Avatar
} from 'antd';
import { 
  PlusOutlined, 
  UploadOutlined, 
  EditOutlined, 
  DeleteOutlined,
  EyeOutlined,
  ShareAltOutlined,
  BarChartOutlined,
  LogoutOutlined,
  UserOutlined,
  BookOutlined,
  ClockCircleOutlined,
  LineChartOutlined,
  RobotOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import AnswerConfig from './AnswerConfig';
import TestPreview from './TestPreview';
import AIAssistant from './AIAssistant';

const { TabPane } = Tabs;
const { Option } = Select;

interface Test {
  id: string;
  title: string;
  description: string;
  file_path: string;
  file_type: string;
  question_count?: number;
  created_at: string;
  questions?: any[];
  start_time?: string;
  end_time?: string;
  time_limit?: number;
}

const TeacherDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [tests, setTests] = useState<Test[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingTest, setEditingTest] = useState<Test | null>(null);
  const [previewTest, setPreviewTest] = useState<Test | null>(null);
  const [editingQuestions, setEditingQuestions] = useState<Test | null>(null);
  const [teacher, setTeacher] = useState<any>(null);
  const [form] = Form.useForm();
  const [timeSettingsModal, setTimeSettingsModal] = useState<Test | null>(null);
  const [timeForm] = Form.useForm();
  const [aiVisible, setAiVisible] = useState(false);

  // 检查登录状态
  useEffect(() => {
    const token = localStorage.getItem('token');
    const teacherData = localStorage.getItem('teacher');
    
    if (!token || !teacherData) {
      navigate('/teacher/login');
      return;
    }

    setTeacher(JSON.parse(teacherData));
    
    // 设置axios默认header
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    
    // 验证token是否有效
    fetchTeacherInfo();
    fetchTests();
  }, [navigate]);

  // 获取教师信息
  const fetchTeacherInfo = async () => {
    try {
      const response = await axios.get('/api/teachers/me');
      setTeacher(response.data);
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        // Token无效，清除登录信息并跳转到登录页
        localStorage.removeItem('token');
        localStorage.removeItem('teacher');
        navigate('/teacher/login');
        message.error('Session expired. Please log in again.');
      }
    }
  };

  // Fetch test list
  const fetchTests = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/tests');
      setTests(response.data);
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('token');
        localStorage.removeItem('teacher');
        navigate('/teacher/login');
        message.error('Session expired. Please log in again.');
      } else {
        message.error('Failed to fetch test list');
      }
    } finally {
      setLoading(false);
    }
  };

  // 登出
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('teacher');
    delete axios.defaults.headers.common['Authorization'];
    navigate('/teacher/login');
    message.success('Logged out successfully');
  };

  // File upload
  const handleUpload = async (file: File) => {
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post('/api/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      message.success('File uploaded successfully');
      return response.data.file;
    } catch (error) {
      message.error('File upload failed');
      return null;
    } finally {
      setUploading(false);
    }
  };

  // Create test
  const handleCreateTest = async (values: any) => {
    try {
      const file = values.file?.[0]?.originFileObj;
      
      if (!file) {
        message.error('Please upload a file first');
        return;
      }
      
      let fileInfo = null;
      fileInfo = await handleUpload(file);
      if (!fileInfo) {
        message.error('File upload failed');
        return;
      }

      const testData = {
        title: values.title,
        description: values.description,
        file_path: fileInfo?.path || '',
        file_type: fileInfo?.mimetype || '',
        question_count: values.question_count || 0
      };

      const response = await axios.post('/api/tests', testData);
      message.success('Test created successfully');
      setIsModalVisible(false);
      form.resetFields();
      fetchTests();
      
      // Automatically enter answer configuration mode
      const newTestId = response.data.testId;
      const newTest = {
        id: newTestId,
        title: values.title,
        description: values.description,
        file_path: fileInfo?.path || '',
        file_type: fileInfo?.mimetype || '',
        question_count: values.question_count || 0,
        created_at: new Date().toISOString()
      };
      setEditingQuestions(newTest);
    } catch (error) {
      console.error('Failed to create test:', error);
      message.error('Failed to create test');
    }
  };

  // Edit test
  const handleEditTest = (test: Test) => {
    setEditingTest(test);
    setIsModalVisible(true);
    form.setFieldsValue({
      title: test.title,
      description: test.description
    });
  };

  // Preview test
  const handlePreviewTest = async (test: Test) => {
    try {
      const response = await axios.get(`/api/tests/${test.id}`);
      setPreviewTest(response.data);
    } catch (error) {
      message.error('Failed to fetch test details');
    }
  };

  // Get share link
  const getShareLink = (testId: string) => {
    const link = `${window.location.origin}/test/${testId}`;
    navigator.clipboard.writeText(link);
    message.success('Share link copied to clipboard');
  };

  // View statistics
  const handleViewStatistics = (testId: string) => {
    window.open(`/statistics/${testId}`, '_blank');
  };

  // Open time settings modal
  const handleTimeSettings = (test: Test) => {
    setTimeSettingsModal(test);
    timeForm.setFieldsValue({
      start_time: test.start_time ? test.start_time.slice(0, 16) : '',
      end_time: test.end_time ? test.end_time.slice(0, 16) : '',
      time_limit: test.time_limit || ''
    });
  };

  // Save time settings
  const handleSaveTimeSettings = async (values: any) => {
    if (!timeSettingsModal) return;
    
    try {
      await axios.put(`/api/tests/${timeSettingsModal.id}/time-settings`, {
        start_time: values.start_time ? new Date(values.start_time).toISOString() : null,
        end_time: values.end_time ? new Date(values.end_time).toISOString() : null,
        time_limit: values.time_limit ? parseInt(values.time_limit) : null
      });
      message.success('Time settings saved');
      setTimeSettingsModal(null);
      fetchTests();
    } catch (error) {
      message.error('Failed to save time settings');
    }
  };

  // View advanced statistics
  const handleAdvancedStats = (testId: string) => {
    window.open(`/statistics/${testId}/advanced`, '_blank');
  };

  // Edit questions
  const handleEditQuestions = async (test: Test) => {
    try {
      const response = await axios.get(`/api/tests/${test.id}`);
      setEditingQuestions(response.data);
    } catch (error) {
      message.error('Failed to fetch test details');
    }
  };

  const columns = [
    {
      title: 'Test Title',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'File Type',
      dataIndex: 'file_type',
      key: 'file_type',
      render: (type: string) => {
        const colorMap: { [key: string]: string } = {
          'application/pdf': 'red',
          'image/': 'green',
          'application/msword': 'blue',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'blue'
        };
        
        let color = 'default';
        for (const [key, value] of Object.entries(colorMap)) {
          if (type.includes(key)) {
            color = value;
            break;
          }
        }
        
        return <Tag color={color}>{type.split('/')[1]?.toUpperCase() || 'UNKNOWN'}</Tag>;
      }
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleString()
    },
    {
      title: 'Actions',
      key: 'action',
      render: (_: any, record: Test) => (
        <Space size="middle">
          <Button 
            type="link" 
            icon={<EyeOutlined />}
            onClick={() => handlePreviewTest(record)}
          >
            Preview
          </Button>
          <Button 
            type="link" 
            icon={<EditOutlined />}
            onClick={() => handleEditTest(record)}
          >
            Edit
          </Button>
          <Button 
            type="link" 
            onClick={() => handleEditQuestions(record)}
          >
            Questions
          </Button>
          <Button 
            type="link" 
            icon={<ShareAltOutlined />}
            onClick={() => getShareLink(record.id)}
          >
            Share
          </Button>
          <Button 
            type="link" 
            icon={<BarChartOutlined />}
            onClick={() => handleViewStatistics(record.id)}
          >
            Statistics
          </Button>
          <Button 
            type="link" 
            icon={<LineChartOutlined />}
            onClick={() => handleAdvancedStats(record.id)}
          >
            Advanced
          </Button>
          <Button 
            type="link" 
            icon={<ClockCircleOutlined />}
            onClick={() => handleTimeSettings(record)}
          >
            Time
          </Button>
        </Space>
      ),
    },
  ];

  const userMenuItems = [
    {
      key: 'logout',
      label: (
        <span onClick={handleLogout}>
          <LogoutOutlined /> Log Out
        </span>
      )
    }
  ];

  return (
    <div>
      <div className="header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1>Teacher Dashboard</h1>
            <p>Create and manage your online exams</p>
          </div>
          <Space>
            <Button
              icon={<RobotOutlined />}
              onClick={() => setAiVisible(true)}
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                fontWeight: 500
              }}
            >
              AI Assistant
            </Button>
            {teacher && (
              <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  cursor: 'pointer',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  background: '#f0f0f0'
                }}>
                  <Avatar icon={<UserOutlined />} style={{ marginRight: '8px' }} />
                  <span>{teacher.name || teacher.username}</span>
                </div>
              </Dropdown>
            )}
          </Space>
        </div>
      </div>
      
      <div className="container">
        <Card>
          <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2>My Tests</h2>
            <Space>
              <Button 
                icon={<BookOutlined />}
                onClick={() => navigate('/teacher/question-bank')}
              >
                Question Bank
              </Button>
              <Button 
                onClick={() => navigate('/teacher/students')}
              >
                View All Students
              </Button>
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                onClick={() => setIsModalVisible(true)}
              >
                Create New Test
              </Button>
            </Space>
          </div>
          
          <Table
            columns={columns}
            dataSource={tests}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 10 }}
          />
        </Card>

        {/* Create/Edit Test Modal */}
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span>{editingTest ? "✏️ Edit Test" : "📄 Create New Test"}</span>
              {!editingTest && (
                <span style={{ fontSize: '12px', color: '#666', fontWeight: 'normal' }}>
                  (Will automatically enter question settings after upload)
                </span>
              )}
            </div>
          }
          open={isModalVisible}
          onCancel={() => {
            setIsModalVisible(false);
            setEditingTest(null);
            form.resetFields();
          }}
          footer={null}
          width={600}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleCreateTest}
          >
            <div style={{ 
              background: '#f6ffed', 
              padding: '12px', 
              borderRadius: '6px', 
              marginBottom: '20px',
              border: '1px solid #b7eb8f'
            }}>
              <div style={{ color: '#52c41a', fontWeight: 'bold', marginBottom: '8px' }}>
                💡 Tip
              </div>
              <div style={{ color: '#666', fontSize: '13px', lineHeight: '1.5' }}>
                After uploading the file, enter the number of questions. The system will automatically enter the answer configuration interface, where you only need to configure the correct answer for each question.
              </div>
            </div>

            <Form.Item
              name="title"
              label="Test Title"
              rules={[{ required: true, message: 'Please enter test title' }]}
            >
              <Input placeholder="Enter test title" />
            </Form.Item>
            
            <Form.Item
              name="description"
              label="Test Description"
            >
              <Input.TextArea 
                placeholder="Enter test description (optional)" 
                rows={3}
              />
            </Form.Item>

            <Form.Item
              name="question_count"
              label="Number of Questions"
              rules={[{ required: true, message: 'Please enter number of questions' }]}
            >
              <Input 
                type="number" 
                min={1} 
                max={100}
                placeholder="Enter number of questions" 
                style={{ width: '200px' }}
              />
            </Form.Item>
            
            <Form.Item
              name="file"
              label="Upload File"
              rules={[{ required: true, message: 'Please upload a file' }]}
              valuePropName="fileList"
              getValueFromEvent={(e: any) => {
                if (Array.isArray(e)) {
                  return e;
                }
                return e && e.fileList;
              }}
            >
              <div>
                <Upload
                  beforeUpload={() => false}
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx"
                  maxCount={1}
                  onChange={(info) => {
                    form.setFieldsValue({ file: info.fileList });
                  }}
                >
                  <Button icon={<UploadOutlined />} loading={uploading} size="large" style={{ width: '100%' }}>
                    Select File (PDF/Image/Word)
                  </Button>
                </Upload>
                <div style={{ color: '#999', fontSize: '12px', marginTop: '8px' }}>
                  Supports PDF, images, and Word documents. Maximum file size: 10MB
                </div>
              </div>
            </Form.Item>
            
            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={uploading}>
                  {editingTest ? 'Update' : 'Create'}
                </Button>
                <Button onClick={() => setIsModalVisible(false)}>
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Test Preview Modal */}
        <Modal
          title="Test Preview"
          open={!!previewTest}
          onCancel={() => setPreviewTest(null)}
          footer={null}
          width={800}
        >
          {previewTest && <TestPreview test={previewTest} />}
        </Modal>

        {/* Answer Configuration Modal */}
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span>🔧 Answer Configuration</span>
              {editingQuestions && (
                <span style={{ fontSize: '14px', color: '#666', fontWeight: 'normal' }}>
                  - {editingQuestions.title}
                </span>
              )}
            </div>
          }
          open={!!editingQuestions}
          onCancel={() => setEditingQuestions(null)}
          footer={null}
          width={1200}
          style={{ top: 20 }}
        >
          {editingQuestions && (
            <div>
              <div style={{ 
                background: '#f0f8ff', 
                padding: '15px', 
                borderRadius: '6px', 
                marginBottom: '20px',
                border: '1px solid #d6e4ff'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                  <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#1890ff' }}>📋 Configuration Instructions</span>
                </div>
                <div style={{ color: '#666', lineHeight: '1.6' }}>
                  <p>• Students will view the original document you uploaded to answer questions</p>
                  <p>• You only need to configure the correct answer for each question (multiple choice, true/false, fill-in-the-blank)</p>
                  <p>• The system will automatically grade student answers based on the standard answers</p>
                  <p>• You can set points and knowledge tags for each question</p>
                </div>
              </div>
              <AnswerConfig 
                testId={editingQuestions.id} 
                questionCount={editingQuestions.question_count || 0}
                onComplete={() => {
                  setEditingQuestions(null);
                  message.success('Answer configuration completed!');
                }}
              />
            </div>
          )}
        </Modal>

        {/* AI Assistant */}
        <AIAssistant
          visible={aiVisible}
          onClose={() => setAiVisible(false)}
          title="AI Teaching Assistant"
        />

        {/* Time Settings Modal */}
        <Modal
          title={<span><ClockCircleOutlined /> Time Settings - {timeSettingsModal?.title}</span>}
          open={!!timeSettingsModal}
          onCancel={() => setTimeSettingsModal(null)}
          footer={null}
          width={500}
        >
          <Form form={timeForm} layout="vertical" onFinish={handleSaveTimeSettings}>
            <Form.Item
              name="time_limit"
              label="Time Limit (minutes)"
              extra="Leave empty for no time limit"
            >
              <Input type="number" placeholder="e.g., 60" min={1} />
            </Form.Item>
            <Form.Item
              name="start_time"
              label="Start Time"
              extra="Students cannot start the test before this time"
            >
              <Input type="datetime-local" />
            </Form.Item>
            <Form.Item
              name="end_time"
              label="End Time"
              extra="Students cannot start the test after this time"
            >
              <Input type="datetime-local" />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit">
                  Save Settings
                </Button>
                <Button onClick={() => setTimeSettingsModal(null)}>
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default TeacherDashboard;
